import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { TextArea } from "@/components/base/textarea/textarea";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { Modal } from "@/components/application/modals/modal-controlled";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { ApiError, apiCall } from "../../lib/apiClient";

/**
 * Papírový souhlas — upload (UC-009 push-assist, R-026 hard block).
 *
 * GlobalAdmin (Petr) spravuje PDF scany papírových consent formulářů pro
 * non-tech personu (Josef). Bez valid paper consent backend blokuje QR generaci
 * (PushAssistConsentRequiredException → CONSENT_PDF_REQUIRED).
 *
 * Backend: PushAssistRoutes
 *   GET  /api/admin/push-assist                              — users s push_assist_mode=TRUE
 *   POST /api/admin/push-assist/{userId}/paper-consent       — multipart PDF (consent_pdf part)
 *   POST /api/admin/push-assist/{userId}/withdraw-consent    — { reason }
 */

interface PushAssistUserRow {
  user_id: string;
  tenant_id: string;
  email: string;
  display_name: string;
  role: string;
  consent_status: string;
  has_valid_paper_consent: boolean;
  latest_consent_pdf_id: string | null;
}

interface PushAssistListResponse {
  users: PushAssistUserRow[];
}

interface UploadResponse {
  stored_file_id: string;
  storage_key: string;
  sha256: string;
  size_bytes: number;
  uploaded_at: string;
}

const MAX_PDF_BYTES = 10 * 1024 * 1024; // 10 MB — UI guardrail; backend má vlastní limit.

export function ConsentUploadsPage() {
  const [users, setUsers] = useState<PushAssistUserRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [uploadFor, setUploadFor] = useState<PushAssistUserRow | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [withdrawFor, setWithdrawFor] = useState<PushAssistUserRow | null>(null);
  const [withdrawReason, setWithdrawReason] = useState<string>("");
  const [withdrawing, setWithdrawing] = useState<boolean>(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await apiCall<PushAssistListResponse>("/api/admin/push-assist");
      setUsers(resp.users);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení push-assist uživatelů selhalo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const openUpload = useCallback((row: PushAssistUserRow) => {
    setUploadFor(row);
    setUploadFile(null);
    setUploadError(null);
    setUploadResult(null);
  }, []);

  const closeUpload = useCallback(() => {
    if (uploading) return;
    setUploadFor(null);
    setUploadFile(null);
    setUploadError(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [uploading]);

  const submitUpload = useCallback(async () => {
    if (!uploadFor || !uploadFile) {
      setUploadError("Vyber PDF soubor s naskenovaným consentem.");
      return;
    }
    if (uploadFile.type && uploadFile.type !== "application/pdf") {
      setUploadError(`Očekává se application/pdf, dostali jsme ${uploadFile.type}.`);
      return;
    }
    if (uploadFile.size > MAX_PDF_BYTES) {
      setUploadError(
        `Soubor je příliš velký (${(uploadFile.size / 1024 / 1024).toFixed(1)} MB, max 10 MB).`,
      );
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append("consent_pdf", uploadFile, uploadFile.name);
      const result = await apiCall<UploadResponse>(
        `/api/admin/push-assist/${uploadFor.user_id}/paper-consent`,
        { method: "POST", body: form },
      );
      setUploadResult(result);
      await fetchUsers();
    } catch (e) {
      if (e instanceof ApiError) {
        setUploadError(mapUploadError(e));
      } else {
        setUploadError(e instanceof Error ? e.message : "Upload selhal");
      }
    } finally {
      setUploading(false);
    }
  }, [uploadFor, uploadFile, fetchUsers]);

  const openWithdraw = useCallback((row: PushAssistUserRow) => {
    setWithdrawFor(row);
    setWithdrawReason("");
    setWithdrawError(null);
  }, []);

  const closeWithdraw = useCallback(() => {
    if (withdrawing) return;
    setWithdrawFor(null);
    setWithdrawReason("");
    setWithdrawError(null);
  }, [withdrawing]);

  const submitWithdraw = useCallback(async () => {
    if (!withdrawFor) return;
    if (!withdrawReason.trim()) {
      setWithdrawError("Důvod odvolání je povinný (audit log).");
      return;
    }
    setWithdrawing(true);
    setWithdrawError(null);
    try {
      await apiCall(`/api/admin/push-assist/${withdrawFor.user_id}/withdraw-consent`, {
        method: "POST",
        body: JSON.stringify({ reason: withdrawReason.trim() }),
      });
      await fetchUsers();
      closeWithdraw();
    } catch (e) {
      if (e instanceof ApiError) {
        setWithdrawError(e.message || `HTTP ${e.status}`);
      } else {
        setWithdrawError(e instanceof Error ? e.message : "Odvolání selhalo");
      }
    } finally {
      setWithdrawing(false);
    }
  }, [withdrawFor, withdrawReason, fetchUsers, closeWithdraw]);

  const columns = useMemo<Column<PushAssistUserRow>[]>(
    () => [
      {
        key: "user",
        header: "Uživatel",
        cell: (row) => (
          <div>
            <strong>{row.display_name || row.email}</strong>
            <div className="text-xs text-tertiary">{row.email}</div>
          </div>
        ),
      },
      {
        key: "role",
        header: "Role",
        cell: (row) => <Badge color="gray">{row.role}</Badge>,
      },
      {
        key: "consent",
        header: "Stav souhlasu",
        cell: (row) => (
          <Badge color={consentBadgeColor(row.consent_status)}>{row.consent_status}</Badge>
        ),
      },
      {
        key: "paper",
        header: "Papírový souhlas",
        cell: (row) => {
          if (!row.has_valid_paper_consent) {
            return <Badge color="warning">Chybí</Badge>;
          }
          return (
            <div>
              <Badge color="success">Platný</Badge>
              {row.latest_consent_pdf_id && (
                <div className="text-xs text-tertiary" title="ID souboru">
                  id {row.latest_consent_pdf_id.slice(0, 8)}…
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "actions",
        header: <span className="sr-only">Akce</span>,
        cell: (row) => (
          <div className="flex gap-2">
            <Button
              size="sm"
              color="secondary"
              onPress={() => openUpload(row)}
              aria-label={`Nahrát papírový souhlas pro ${row.email}`}
            >
              {row.has_valid_paper_consent ? "Nahrát nový" : "Nahrát PDF"}
            </Button>
            <Button
              size="sm"
              color="secondary"
              onPress={() => openWithdraw(row)}
              isDisabled={!row.has_valid_paper_consent}
              aria-label={`Odvolat souhlas pro ${row.email}`}
            >
              Odvolat
            </Button>
          </div>
        ),
      },
    ],
    [openUpload, openWithdraw],
  );

  return (
    <section aria-labelledby="consent-uploads-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="consent-uploads-title" className="text-display-xs font-bold text-primary m-0">
          Papírový souhlas
        </h2>
        <p className="text-md text-tertiary m-0">
          Pro uživatele v režimu push-assist (typicky Josef) musí být v systému naskenovaný
          podepsaný souhlas. Bez platného souhlasu nelze generovat platební QR kód.
        </p>
      </header>

      {loadError && <InlineAlert variant="error">{loadError}</InlineAlert>}

      <Card>
        <CardHeader title="Push-assist uživatelé" />
        <CardBody>
          <DataTable
            caption="Uživatelé v push-assist módu (paper consent management)"
            columns={columns}
            rows={users}
            rowKey={(row) => row.user_id}
            loading={loading}
            emptyMessage="Žádní uživatelé v push-assist módu."
          />
        </CardBody>

      {uploadFor && (
        <Modal
          open
          title={`Nahrát papírový consent — ${uploadFor.email}`}
          onClose={closeUpload}
          footer={
            <>
              <Button color="secondary" onPress={closeUpload} isDisabled={uploading}>
                {uploadResult ? "Zavřít" : "Zrušit"}
              </Button>
              {!uploadResult && (
                <Button
                  color="primary"
                  onPress={submitUpload}
                  isDisabled={uploading || !uploadFile}
                >
                  {uploading ? "Nahrávám…" : "Nahrát"}
                </Button>
              )}
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitUpload();
            }}
            aria-live="polite"
          >
            <p className="text-tertiary m-0">
              PDF se ověří, zkontroluje antivirem a uloží zašifrované. Server
              nikdy neukládá obsah souboru do logů.
            </p>
            <div className="mt-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-secondary">
                  Naskenované PDF s podepsaným souhlasem
                  <span aria-hidden="true"> *</span>
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  required
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setUploadFile(f);
                    setUploadError(null);
                  }}
                  disabled={uploading || !!uploadResult}
                  aria-label="PDF s naskenovaným papírovým souhlasem"
                  className="text-sm text-secondary"
                />
              </label>
              {uploadFile && (
                <p className="text-xs text-tertiary mt-2">
                  {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            {uploadResult && (
              <div className="mt-4">
                <InlineAlert variant="success">
                  Nahráno. <code>id={uploadResult.stored_file_id.slice(0, 8)}…</code>{" "}
                  kontrolní součet <code>{uploadResult.sha256.slice(0, 12)}…</code>{" "}
                  ({(uploadResult.size_bytes / 1024).toFixed(1)} KB)
                </InlineAlert>
              </div>
            )}
            {uploadError && (
              <div className="mt-4">
                <InlineAlert variant="error">{uploadError}</InlineAlert>
              </div>
            )}
          </form>
        </Modal>
      )}

      {withdrawFor && (
        <Modal
          open
          title={`Odvolat papírový consent — ${withdrawFor.email}`}
          onClose={closeWithdraw}
          footer={
            <>
              <Button color="secondary" onPress={closeWithdraw} isDisabled={withdrawing}>
                Zrušit
              </Button>
              <Button
                color="primary-destructive"
                onPress={submitWithdraw}
                isDisabled={withdrawing}
              >
                {withdrawing ? "Odvolávám…" : "Odvolat"}
              </Button>
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitWithdraw();
            }}
            aria-live="polite"
          >
            <InlineAlert variant="warning">
              Odvolání se zapíše do auditu a zneplatní stávající souhlas.
              Generování platebních QR kódů pro tohoto uživatele bude zablokované,
              dokud nebude nahrán nový podepsaný souhlas.
            </InlineAlert>
            <div className="mt-4">
              <TextArea
                label="Důvod odvolání"
                isRequired
                hint="Konkrétní důvod — žádost subjektu, novější verze souhlasu, chyba ve scanu, ..."
                value={withdrawReason}
                onChange={setWithdrawReason}
                rows={3}
                isDisabled={withdrawing}
              />
            </div>
            {withdrawError && (
              <div className="mt-4">
                <InlineAlert variant="error">{withdrawError}</InlineAlert>
              </div>
            )}
          </form>
        </Modal>
      )}
      </Card>
    </section>
  );
}

function consentBadgeColor(status: string): "gray" | "success" | "warning" | "error" {
  switch (status) {
    case "accepted":
    case "active":
      return "success";
    case "pending":
    case "re_prompt_required":
      return "warning";
    case "withdrawn":
    case "revoked":
      return "error";
    default:
      return "gray";
  }
}

function mapUploadError(e: ApiError): string {
  switch (e.errorCode) {
    case "CONSENT_PDF_INFECTED":
      return "V souboru byl detekován malware. Nahrání zamítnuto.";
    case "MALWARE_SCANNER_UNAVAILABLE":
      return "Antivirová kontrola není momentálně dostupná. Zkus později.";
    case "STORAGE_UNAVAILABLE":
      return `Úložiště souborů není momentálně dostupné: ${e.message}`;
    case "PUSH_ASSIST_INVALID_PAYLOAD":
      return `Neplatný požadavek: ${e.message}`;
    case "PUSH_ASSIST_USER_NOT_FOUND":
      return "Uživatel nenalezen.";
    case "USER_NOT_IN_PUSH_ASSIST_MODE":
      return "Uživatel není v režimu push-assist — nejdřív ho zapni v jeho profilu.";
    default:
      return e.message || `HTTP ${e.status}`;
  }
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/consent-uploads",
  component: ConsentUploadsPage,
});
